/**
 * Admin · Groups — derived from children.group_name.
 * Each group shows its size, member portraits, and assigned caregivers.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAdminWorkspace, AdminHeader, AdminEmpty, AdminEmptyCard } from "./_shared";

export default function AdminGroups() {
  const { workspace, loading: wsLoading } = useAdminWorkspace();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspace) return;
    let alive = true;
    api
      .get(`/admin/workspaces/${workspace.id}/groups`)
      .then((r) => alive && setGroups(r.data || []))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [workspace?.id]);

  if (wsLoading) return null;
  if (!workspace)
    return (
      <AdminEmptyCard
        testid="admin-groups-no-ws"
        eyebrow="Groups & classrooms"
        title="Workspaces hold groups."
        body="Once you've created a center, groups appear automatically as you assign a `group name` to each child."
        action={
          <a href="/workspace" data-testid="admin-groups-create-workspace" className="btn-primary">
            Create a workspace
          </a>
        }
      />
    );

  return (
    <div data-testid="admin-groups-screen">
      <AdminHeader
        eyebrow="Groups & classrooms"
        title={`${groups.length} ${groups.length === 1 ? "room" : "rooms"}`}
      />
      {loading ? (
        <AdminEmpty testid="admin-groups-loading">A quiet moment…</AdminEmpty>
      ) : groups.length === 0 ? (
        <AdminEmptyCard
          testid="admin-groups-empty"
          eyebrow="Groups appear here"
          title="No groups yet."
          body="Groups are derived from your children's `group name` field — assign a group on each child's profile and they'll fold into a calm classroom view here."
          action={
            <a href="/workspace" data-testid="admin-groups-assign" className="btn-ghost">
              Open workspace
            </a>
          }
        />
      ) : (
        <ul
          data-testid="admin-groups-list"
          className="mt-6 grid sm:grid-cols-2 gap-4"
        >
          {groups.map((g) => (
            <li
              key={g.name}
              data-testid={`admin-group-${g.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className="rounded-3xl bg-white border hairline p-6"
            >
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="font-serif text-xl tracking-tight text-[#171717]">
                  {g.name}
                </h3>
                <span
                  className="font-serif text-3xl text-[#1A1B2E] tabular-nums"
                  data-testid={`admin-group-${g.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}-size`}
                >
                  {g.size}
                </span>
              </div>
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#525252] mt-1">
                children
              </p>
              {g.children?.length > 0 && (
                <div
                  className="mt-5 flex -space-x-2"
                  data-testid={`admin-group-${g.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}-avatars`}
                >
                  {g.children.slice(0, 8).map((c) => (
                    <span
                      key={c.id}
                      title={c.name}
                      className="h-9 w-9 rounded-full border-2 border-white bg-[#F2EFEA] overflow-hidden flex items-center justify-center"
                    >
                      {c.portrait_url ? (
                        <img
                          src={c.portrait_url}
                          alt={c.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="font-serif italic text-sm text-[#8A9984]">
                          {c.name[0]}
                        </span>
                      )}
                    </span>
                  ))}
                  {g.children.length > 8 && (
                    <span className="h-9 w-9 rounded-full border-2 border-white bg-[#F2EFEA] flex items-center justify-center text-xs text-[#525252] font-medium">
                      +{g.children.length - 8}
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
